import React, { useCallback, useContext, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import { Portal } from '@mui/base/Portal';
import Progress from 'studio/components/Progress';
import styles from 'studio/pages/Editor/Editor.styles';
import Tree from 'components/Tree/Tree';
import FolderIcon from '@mui/icons-material/FolderRounded';
import ConceptTree from 'studio/pages/Editor/ConceptTree/ConceptTree';
import { EditorContext, treeTypes } from 'studio/pages/Editor/Editor';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import { sortBy } from 'utils/arrayUtils';
import { useEditorUtils } from 'studio/pages/Editor/EditorUtils';
import SyncModal from 'studio/pages/Editor/SyncModal';
import EditorLeftPanelActionBar from 'studio/pages/Editor/EditorLeftPanelActionBar';

/**
 * Component for the left panel of the editor / studio
 *
 * It is part of the editor because it shares a lot of state with it, but this could be refactored (it is visually outside the editor and uses a portal).
 */
const EditorLeftPanel = ({ classes }) => {
  const {
    knowledgeGraph,
    navigation: { openInEditor },
    fileState: { files },
    fileOperations: { getFiles },
    refs: { menuContainerRef, conceptTreeRef },
    deletion: { setFilesToDelete },
    sync: { openSyncDialog },
    trees: { selectedConceptTreeDefinition }
  } = useContext(EditorContext);

  const { operationAffectingMultipleFiles } = useEditorUtils();
  // the type of the tree that is currently shown
  const [treeType, setTreeType] = useState(treeTypes.concept);

  // the items for the files tree
  const fileTreeItems = useMemo(() => {
    const rootTreeItem = { children: [] };
    if (files) {
      // iterate over all file paths
      Object.keys(files).forEach(path => {
        // split the path into elements
        const pathElements = path.replace(/^\/|\/$/g, '').split('/');
        let currentTreeItem = rootTreeItem;
        // iterate over all path elements
        pathElements.forEach((pathElement, i) => {
          // assemble the current path from the root to the current element
          const currentPath = pathElements.slice(0, i + 1).join('/');
          // find the tree item corresponding to the current path, if it already got created...
          let item = currentTreeItem.children.find(item => item.id === currentPath);
          if (!item) {
            // ...else create it as a leaf (file) tree item
            item = {
              id: currentPath,
              label: pathElement,
              icon: DescriptionIcon,
              children: [],
              onClick: () => openInEditor(currentPath),
              actions: [
                {
                  icon: DeleteIcon,
                  label: 'Delete file',
                  onClick: _ => {
                    // find all files at and below the current path
                    const filesToDelete = Object.values(files).filter(
                      file => file.data.path === currentPath || file.data.path.startsWith(currentPath + '/')
                    );
                    setFilesToDelete(filesToDelete);
                  }
                },
                {
                  icon: RefreshIcon,
                  label: 'Reload files',
                  onClick: _ => getFiles()
                }
              ]
            };
            // ...and add it as a child to its parent tree item
            currentTreeItem.children.push(item);
            // ...and change its parent tree item to be a non-leaf folder tree item
            currentTreeItem.icon = FolderIcon;
            currentTreeItem.onClick = null;
            currentTreeItem.actions && (currentTreeItem.actions[0].label = 'Delete folder');
          }
          currentTreeItem = item;
        });
      });
      // sort the tree items
      const sort = treeItems => {
        treeItems.forEach(treeItem => (treeItem.children = treeItem.children && sort(treeItem.children)));
        // sorts files before folders, then by file/folder name
        return sortBy(treeItems, treeItem => [treeItem.children.length && 1, treeItem.label].join());
      };
      return sort(rootTreeItem.children);
    }
  }, [files, openInEditor, setFilesToDelete]);

  // is called when a concept is about to be deleted and checks if the file(s) affected by the deletion are modified
  const onDeleteConcept = useCallback(
    (type, identifier, affectedFileIds, deleteConcept) => {
      operationAffectingMultipleFiles(
        `Delete ${type}`,
        `Deleting the ${type} <code>${identifier}</code>`,
        `deleting the ${type}`,
        affectedFileIds,
        deleteConcept
      );
    },
    [operationAffectingMultipleFiles]
  );

  return (
    <>
      {!!menuContainerRef.current && (
        <Portal container={menuContainerRef.current}>
          <EditorLeftPanelActionBar treeType={treeType} setTreeType={setTreeType} />

          <div
            className={classes.treeContainer}
            style={{ display: treeType === treeTypes.concept ? 'block' : 'none' }}
            data-test="editorTreeContainer"
          >
            <ConceptTree
              ref={conceptTreeRef}
              className={classes.tree}
              knowledgeGraph={knowledgeGraph}
              selectedDefinition={selectedConceptTreeDefinition}
              open={openInEditor}
              onDelete={onDeleteConcept}
              openSyncDialog={openSyncDialog}
            />
          </div>
          <div
            className={classes.treeContainer}
            style={{ display: treeType === treeTypes.file ? 'block' : 'none' }}
            data-test="editorTreeContainer"
          >
            {!files ? <Progress /> : <Tree className={classes.tree} items={fileTreeItems} />}
          </div>

          {/* Sync modal needs to be available independent of if the editor is open, thus it's here in the left panel */}
          <SyncModal />
        </Portal>
      )}
    </>
  );
};

export default withStyles(styles)(EditorLeftPanel);
